<template>
  <BottomSheetModal
    v-model="open"
    hide-confirm
    :hide-header="true"
    @close="onClose"
  >
    <div class="report-sheet">
      <!-- LIST VIEW -->
      <Transition name="rs-slide" mode="out-in">
        <div v-if="view === 'list'" key="list" class="rs-view">
          <button class="rs-cancel" @click="onClose">{{ t('common.cancel') }}</button>
          <h2 class="rs-title">{{ t('report.chooseTitle') }}</h2>
          <p class="rs-intro">{{ t('report.chooseIntro') }}</p>

          <!-- Target preview -->
          <div v-if="target" class="rs-target">
            <div class="rs-target-kind">{{ t('report.reporting') }}</div>
            <div class="rs-target-row">
              <div class="rs-target-img" :class="{ 'rs-target-img--round': target.kind === 'user' }">
                <img v-if="target.image" :src="target.image" :alt="target.name" />
                <span v-else class="rs-target-fallback">{{ target.initials || '?' }}</span>
              </div>
              <div class="rs-target-info">
                <div class="rs-target-name">{{ target.name }}</div>
                <div class="rs-target-type">{{ target.kindLabel }}</div>
              </div>
            </div>
          </div>
          <div v-else-if="targetLoading" class="rs-target rs-target--loading">
            <v-progress-circular size="20" width="2" indeterminate />
          </div>

          <ul class="rs-list">
            <li
              v-for="cat in categories"
              :key="cat.key"
              class="rs-list-item"
              @click="selectCategory(cat.key)"
            >
              <span class="rs-list-label">{{ t(`report.cats.${cat.key}.label`) }}</span>
              <v-icon size="20" class="rs-list-chev">mdi-chevron-right</v-icon>
            </li>
          </ul>
        </div>

        <!-- DETAIL VIEW -->
        <div v-else key="detail" class="rs-view rs-detail">
          <button class="rs-back" @click="view = 'list'">
            <v-icon size="20">mdi-chevron-left</v-icon>
            <span>{{ t('common.back') }}</span>
          </button>

          <h2 class="rs-title rs-title--detail">
            {{ t(`report.cats.${view}.label`) }}
          </h2>

          <!-- Target preview (detail) -->
          <div v-if="target" class="rs-target rs-target--compact">
            <div class="rs-target-img rs-target-img--sm" :class="{ 'rs-target-img--round': target.kind === 'user' }">
              <img v-if="target.image" :src="target.image" :alt="target.name" />
              <span v-else class="rs-target-fallback">{{ target.initials || '?' }}</span>
            </div>
            <div class="rs-target-info">
              <div class="rs-target-name">{{ target.name }}</div>
              <div class="rs-target-type">{{ target.kindLabel }}</div>
            </div>
          </div>

          <div class="rs-detail-body">
            <p class="rs-detail-lead">
              {{ t(`report.cats.${view}.lead`) }}
            </p>
            <p class="rs-detail-desc">
              {{ t(`report.cats.${view}.desc`) }}
            </p>

            <div v-if="examplesText" class="rs-examples">
              <h4 class="rs-examples-title">{{ t('report.examplesTitle') }}</h4>
              <p class="rs-examples-text">{{ examplesText }}</p>
            </div>

            <!-- Optional note -->
            <div class="rs-note-wrap">
              <label class="rs-note-label">{{ t('report.noteLabel') }} <span class="rs-optional">({{ t('common.optional') }})</span></label>
              <textarea
                v-model="noteByCategory[view]"
                rows="3"
                class="rs-note"
                :placeholder="t('report.notePlaceholder')"
                maxlength="2000"
              />
            </div>

            <p v-if="serverError" class="rs-error">{{ serverError }}</p>
            <p v-if="success" class="rs-success">{{ t('report.successText') }}</p>
          </div>

          <button
            type="button"
            class="rs-submit"
            :disabled="loading || success"
            @click="submit"
          >
            <v-progress-circular v-if="loading" size="18" width="2" indeterminate color="white" class="mr-2" />
            <v-icon v-else-if="success" size="20" class="mr-2">mdi-check-circle</v-icon>
            {{ success ? t('report.successBtn') : (loading ? t('common.sending') : t('report.submitBtn')) }}
          </button>
        </div>
      </Transition>
    </div>
  </BottomSheetModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import BottomSheetModal from '@/components/BottomSheetModal.vue'
import axiosInstance from '@/services/axiosInstance'
import { getTeamImage, getLeagueImage, getUserAvatarUrl } from '@/utils/imageUtils'

type CategoryKey =
  | 'harassment'
  | 'selfHarm'
  | 'violence'
  | 'nudity'
  | 'prohibited'
  | 'copyright'
  | 'fraud'
  | 'other'
  | 'illegal'

type TargetKind = 'team' | 'league' | 'academy' | 'user' | 'event' | 'gamecenter' | 'article' | 'message' | 'other'

interface ReportTarget {
  kind: TargetKind
  kindLabel: string
  ref: string
  name: string
  image?: string
  initials?: string
}

interface ReportExtraContext {
  messageId?: string
  messageContent?: string
}

const props = defineProps<{
  modelValue: boolean
  overrideTarget?: ReportTarget | null
  extraContext?: ReportExtraContext | null
}>()
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'submitted'): void
}>()

const { t } = useI18n({ useScope: 'global' })

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'

const open = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

const categories: { key: CategoryKey }[] = [
  { key: 'harassment' },
  { key: 'selfHarm' },
  { key: 'violence' },
  { key: 'nudity' },
  { key: 'prohibited' },
  { key: 'copyright' },
  { key: 'fraud' },
  { key: 'other' },
  { key: 'illegal' },
]

const view = ref<'list' | CategoryKey>('list')
const loading = ref(false)
const success = ref(false)
const serverError = ref('')
const noteByCategory = reactive<Record<string, string>>({})
const currentUrl = ref('')

const target = ref<ReportTarget | null>(null)
const targetLoading = ref(false)

const examplesText = computed(() => {
  if (view.value === 'list') return ''
  const txt = t(`report.cats.${view.value}.examples`)
  return (txt && txt.trim()) ? txt : ''
})

watch(open, (val) => {
  if (val) {
    view.value = 'list'
    loading.value = false
    success.value = false
    serverError.value = ''
    Object.keys(noteByCategory).forEach((k) => delete noteByCategory[k])
    currentUrl.value = typeof window !== 'undefined' ? window.location.href : ''
    if (props.overrideTarget) {
      target.value = { ...props.overrideTarget }
    } else {
      target.value = null
      void detectAndLoadTarget()
    }
  }
})

function selectCategory(k: CategoryKey) {
  view.value = k
  serverError.value = ''
  success.value = false
}

function onClose() {
  if (loading.value) return
  open.value = false
}

/** Parse current URL to figure out what entity is reported and fetch its display data. */
async function detectAndLoadTarget() {
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  if (!path) return

  // Order matters — check more specific patterns first
  const academyM = path.match(/^\/leagues\/([^/?#]+)\/academy/)
  const leagueM = path.match(/^\/leagues\/([^/?#]+)/)
  const teamM = path.match(/^\/teams\/([^/?#]+)/)
  const userM = path.match(/^\/users\/([^/?#]+)/)
  const gcM = path.match(/^\/gamecenter\/([^/?#]+)/)
  const evM = path.match(/^\/(events?|eventdatalog)\/([^/?#]+)/)

  targetLoading.value = true
  try {
    if (academyM) {
      await loadLeague(academyM[1], 'academy', t('report.targets.academy'))
    } else if (teamM) {
      await loadTeam(teamM[1])
    } else if (leagueM) {
      await loadLeague(leagueM[1], 'league', t('report.targets.league'))
    } else if (userM) {
      await loadUser(userM[1])
    } else if (gcM) {
      await loadEvent(gcM[1], 'gamecenter', t('report.targets.gamecenter'))
    } else if (evM) {
      await loadEvent(evM[2], 'event', t('report.targets.event'))
    }
  } catch (err) {
    console.warn('[ReportModal] target detection failed:', err)
  } finally {
    targetLoading.value = false
  }
}

async function loadTeam(id: string) {
  const { data } = await axiosInstance.get(`${API_BASE_URL}/teams/${id}`)
  const t0 = data?.team ?? data
  if (!t0) return
  const imgFile = t0.image || t0.imageUrl
  target.value = {
    kind: 'team',
    kindLabel: t('report.targets.team'),
    ref: t0._id || id,
    name: t0.name || id,
    image: imgFile ? getTeamImage(imgFile, 'w75') : undefined,
    initials: makeInitials(t0.name),
  }
}

async function loadLeague(urlTitle: string, kind: 'league' | 'academy', kindLabel: string) {
  const { data } = await axiosInstance.get(`${API_BASE_URL}/leagues/${urlTitle}`)
  const l0 = data?.league ?? data
  if (!l0) return
  const imgFile = l0.image || l0.imageUrl
  target.value = {
    kind,
    kindLabel,
    ref: l0._id || urlTitle,
    name: l0.name || urlTitle,
    image: imgFile ? getLeagueImage(imgFile, 'h200') : undefined,
    initials: makeInitials(l0.name),
  }
}

async function loadUser(id: string) {
  const { data } = await axiosInstance.get(`${API_BASE_URL}/users/${id}`)
  const u0 = data?.user ?? data
  if (!u0) return
  const fullName = [u0.firstName, u0.lastName].filter(Boolean).join(' ').trim() || u0.nickName || id
  target.value = {
    kind: 'user',
    kindLabel: t('report.targets.user'),
    ref: u0._id || id,
    name: fullName,
    image: getUserAvatarUrl(u0) || undefined,
    initials: ((u0.firstName?.[0] || '') + (u0.lastName?.[0] || '')).toUpperCase() || '?',
  }
}

async function loadEvent(id: string, kind: 'event' | 'gamecenter', kindLabel: string) {
  const { data } = await axiosInstance.get(`${API_BASE_URL}/events/${id}`)
  const e0 = data?.event ?? data
  if (!e0) return
  let image: string | undefined
  // Try to use a related team logo if present (league/team-level event)
  const teamLike = e0._team || e0.homeTeam || e0._homeTeam || e0.meta?.team
  const teamImg = teamLike?.image || teamLike?.imageUrl
  const leagueImg = e0._league?.image || e0._league?.imageUrl
  if (teamImg) image = getTeamImage(teamImg, 'w200')
  else if (leagueImg) image = getLeagueImage(leagueImg, 'h200')
  target.value = {
    kind,
    kindLabel,
    ref: e0._id || id,
    name: e0.title || e0.name || id,
    image,
    initials: makeInitials(e0.title || e0.name || ''),
  }
}

function makeInitials(name?: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || name.charAt(0).toUpperCase()
}

async function submit() {
  if (view.value === 'list' || loading.value) return
  serverError.value = ''
  loading.value = true
  try {
    const formData = new FormData()
    formData.append('type', 'inappropriate')
    formData.append('category', view.value)
    formData.append('description', noteByCategory[view.value]?.trim() || '')
    formData.append('currentUrl', currentUrl.value)
    if (target.value) {
      formData.append('targetKind', target.value.kind)
      formData.append('targetRef', target.value.ref)
      formData.append('targetName', target.value.name)
    }
    if (props.extraContext?.messageId) {
      formData.append('messageId', props.extraContext.messageId)
    }
    if (props.extraContext?.messageContent) {
      formData.append('messageContent', props.extraContext.messageContent)
    }

    await axiosInstance.post(`${API_BASE_URL}/reports`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    success.value = true
    emit('submitted')
    setTimeout(() => {
      open.value = false
    }, 1100)
  } catch (err: any) {
    console.error('[ReportModal] submit failed:', err)
    serverError.value = err?.response?.data?.message || t('report.submitFailed')
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
.report-sheet {
  position: relative;
  min-height: 320px;
  color: rgb(var(--v-theme-sheetText));
}

.rs-view {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 4px 4px 24px;
}

.rs-cancel {
  align-self: flex-start;
  background: none;
  border: none;
  padding: 4px 0;
  margin-bottom: 4px;
  font-size: 16px;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;

  &:active { opacity: 0.6; }
}

.rs-back {
  align-self: flex-start;
  background: none;
  border: none;
  padding: 4px 0;
  margin-bottom: 4px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 16px;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;

  &:active { opacity: 0.6; }
}

.rs-title {
  margin: 4px 0 4px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;

  &--detail {
    font-size: 22px;
  }
}

.rs-intro {
  margin: 0 0 8px;
  font-size: 14px;
  line-height: 1.45;
  color: rgba(var(--v-theme-sheetText), 0.65);
}

/* Target preview */
.rs-target {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(var(--v-theme-sheetText), 0.05);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.08);
  margin-top: 4px;

  &--loading {
    align-items: center;
    justify-content: center;
    min-height: 60px;
  }

  &--compact {
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    margin-top: 0;
  }
}

.rs-target-kind {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(var(--v-theme-sheetText), 0.55);
  font-weight: 700;
}

.rs-target-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rs-target-img {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(var(--v-theme-sheetText), 0.08);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &--sm {
    width: 36px;
    height: 36px;
    border-radius: 8px;
  }

  &--round {
    border-radius: 50%;
  }
}

.rs-target-fallback {
  font-size: 14px;
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.7);
}

.rs-target-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.rs-target-name {
  font-size: 15px;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rs-target-type {
  font-size: 12px;
  color: rgba(var(--v-theme-sheetText), 0.6);
}

.rs-list {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
}

.rs-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 4px;
  border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.1);
  cursor: pointer;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: rgba(var(--v-theme-sheetText), 0.05);
  }
}

.rs-list-label {
  font-size: 16px;
  font-weight: 500;
}

.rs-list-chev {
  opacity: 0.45;
  flex-shrink: 0;
}

/* Detail */
.rs-detail-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 4px;
}

.rs-detail-lead {
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
}

.rs-detail-desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: rgba(var(--v-theme-sheetText), 0.75);
}

.rs-examples {
  border-radius: 12px;
  padding: 12px 14px;
  background: rgba(var(--v-theme-sheetText), 0.05);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.08);
}

.rs-examples-title {
  margin: 0 0 4px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(var(--v-theme-sheetText), 0.55);
  font-weight: 700;
}

.rs-examples-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(var(--v-theme-sheetText), 0.85);
}

.rs-note-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}

.rs-note-label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.85);
}

.rs-optional {
  font-weight: 400;
  color: rgba(var(--v-theme-sheetText), 0.5);
}

.rs-note {
  width: 100%;
  resize: vertical;
  min-height: 80px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.15);
  background: rgba(var(--v-theme-sheetText), 0.05);
  color: rgb(var(--v-theme-sheetText));
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.18s ease, background 0.18s ease;

  &:focus {
    border-color: rgb(var(--v-theme-primary));
    background: rgba(var(--v-theme-sheetText), 0.08);
  }

  &::placeholder {
    color: rgba(var(--v-theme-sheetText), 0.4);
  }
}

.rs-error {
  margin: 4px 0 0;
  padding: 10px 12px;
  background: rgba(229, 57, 53, 0.1);
  border: 1px solid rgba(229, 57, 53, 0.3);
  border-radius: 8px;
  color: #e53935;
  font-size: 13px;
}

.rs-success {
  margin: 4px 0 0;
  padding: 10px 12px;
  background: rgba(76, 175, 80, 0.12);
  border: 1px solid rgba(76, 175, 80, 0.35);
  border-radius: 8px;
  color: #4caf50;
  font-size: 13px;
}

.rs-submit {
  margin-top: 16px;
  width: 100%;
  height: 48px;
  border-radius: 14px;
  border: none;
  background: #e53935;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.15s ease;
  box-shadow: 0 4px 14px rgba(229, 57, 53, 0.35);

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

/* Slide transition between list & detail */
.rs-slide-enter-active,
.rs-slide-leave-active {
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.2s ease;
}
.rs-slide-enter-from {
  transform: translateX(40px);
  opacity: 0;
}
.rs-slide-leave-to {
  transform: translateX(-40px);
  opacity: 0;
}
</style>
